import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { HandCoins, Loader2, AlertCircle, RefreshCw, ChevronLeft, Inbox, Filter } from "lucide-react";
import {
  FUNDING_STATUS_LABEL, FUNDING_STATUS_TONE, formatSAR, formatArabicDateTime,
} from "../donor/fundingLabels";

const STATUS_FILTERS = [
  {v:"",label:"الكل"},
  {v:"SUBMITTED",label:"مُرسَل"},
  {v:"UNDER_REVIEW",label:"قيد المراجعة"},
  {v:"INFORMATION_REQUIRED",label:"بحاجة معلومات"},
  {v:"PLAN_IN_PREPARATION",label:"خطة قيد الإعداد"},
  {v:"PLAN_READY",label:"الخطة جاهزة"},
  {v:"PLAN_PUBLISHED",label:"الخطة منشورة"},
  {v:"REVISION_REQUESTED",label:"طُلبت مراجعة"},
  {v:"CONTRACTING",label:"قيد التعاقد"},
  {v:"DECLINED",label:"مرفوض"},
  {v:"WITHDRAWN",label:"مسحوب"},
];

export default function AdminFundingRequestsList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const params = filter ? `?status_in=${encodeURIComponent(filter)}` : "";
      const { data } = await api.get(`/funding/admin/requests${params}`);
      setRows(Array.isArray(data.requests) ? data.requests : []);
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  return (
    <DashboardLayout>
      <div data-testid="admin-funding-list-page">
        <div className="mb-6">
          <div className="text-xs font-bold uppercase tracking-widest text-indigo-700">إدارة</div>
          <h1 className="mt-2 text-2xl font-heading font-extrabold text-slate-900 flex items-center gap-2">
            <HandCoins className="h-6 w-6 text-amber-700" strokeWidth={1.75} /> طلبات التمويل
          </h1>
          <p className="text-sm text-slate-600 mt-1">راجع الطلبات وأدِرها من هنا: البداية بمراجعة الطلب، مروراً بإعداد الخطة واعتمادها ونشرها.</p>
        </div>

        <div className="mb-4 flex items-center gap-2 flex-wrap" data-testid="admin-filter-bar">
          <Filter className="h-4 w-4 text-slate-500" />
          {STATUS_FILTERS.map(f => (
            <button key={f.v} onClick={()=>setFilter(f.v)}
              data-testid={`admin-filter-${f.v || "ALL"}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                filter === f.v ? "bg-indigo-700 border-indigo-700 text-white" : "bg-white border-slate-200 text-slate-700 hover:border-indigo-400"
              }`}>{f.label}</button>
          ))}
        </div>

        {loading && (
          <div className="flex items-center justify-center py-24" data-testid="admin-list-loading">
            <Loader2 className="h-7 w-7 animate-spin text-indigo-700" />
          </div>
        )}
        {!loading && error && (
          <div className="p-4 rounded-2xl bg-rose-50 border border-rose-200" data-testid="admin-list-error">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-6 w-6 text-rose-700 shrink-0 mt-1" />
              <div className="flex-1">
                <h3 className="font-heading font-bold text-rose-900">تعذّر تحميل الطلبات</h3>
                <p className="text-sm text-rose-800 mt-1">{error}</p>
                <Button variant="outline" onClick={load} className="mt-3" data-testid="admin-list-retry">
                  <RefreshCw className="h-4 w-4 ms-2" /> إعادة المحاولة
                </Button>
              </div>
            </div>
          </div>
        )}
        {!loading && !error && rows && rows.length === 0 && (
          <div className="p-10 rounded-2xl bg-white border border-cream-200 card-shadow text-center" data-testid="admin-list-empty">
            <Inbox className="h-8 w-8 mx-auto text-slate-400" />
            <h3 className="mt-3 font-heading font-bold text-slate-900">لا توجد طلبات ضمن هذا التصنيف</h3>
          </div>
        )}
        {!loading && !error && rows && rows.length > 0 && (
          <>
            <div className="hidden md:block rounded-2xl bg-white border border-cream-200 card-shadow overflow-hidden" data-testid="admin-table-wrap">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 text-xs">
                  <tr>
                    <th className="text-start px-4 py-3 font-semibold">رقم الطلب</th>
                    <th className="text-start px-4 py-3 font-semibold">هدف التمويل</th>
                    <th className="text-start px-4 py-3 font-semibold">القيمة</th>
                    <th className="text-start px-4 py-3 font-semibold">الحالة</th>
                    <th className="text-start px-4 py-3 font-semibold">آخر تحديث</th>
                    <th className="sr-only">فتح</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.id} onClick={()=>navigate(`/dashboard/admin/funding/requests/${r.id}`)}
                        className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                        data-testid={`admin-row-request-${r.id}`}>
                      <td className="px-4 py-3 font-semibold text-slate-900">{r.request_number || <span className="text-slate-400 italic">مسودة</span>}</td>
                      <td className="px-4 py-3 text-slate-700 max-w-[320px] truncate">{r.funding_goal || "—"}</td>
                      <td className="px-4 py-3 text-slate-700 whitespace-nowrap">{formatSAR(r.estimated_amount_or_value)}</td>
                      <td className="px-4 py-3"><StatusPill status={r.status} /></td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{formatArabicDateTime(r.updated_at || r.created_at)}</td>
                      <td className="px-4 py-3 text-slate-400"><ChevronLeft className="h-4 w-4" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="md:hidden space-y-3" data-testid="admin-cards-wrap">
              {rows.map(r => (
                <Link key={r.id} to={`/dashboard/admin/funding/requests/${r.id}`}
                  className="block p-4 rounded-2xl bg-white border border-cream-200 card-shadow hover:border-indigo-300"
                  data-testid={`admin-card-request-${r.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-slate-900 truncate">{r.request_number || <span className="text-slate-400 italic">مسودة</span>}</div>
                      <div className="text-sm text-slate-700 mt-1 line-clamp-2">{r.funding_goal || "—"}</div>
                    </div>
                    <StatusPill status={r.status} />
                  </div>
                  <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
                    <span>{formatSAR(r.estimated_amount_or_value)}</span>
                    <span>{formatArabicDateTime(r.updated_at || r.created_at)}</span>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

function StatusPill({ status }) {
  const label = FUNDING_STATUS_LABEL[status] || status || "—";
  const tone = FUNDING_STATUS_TONE[status] || "bg-slate-100 text-slate-700 border-slate-200";
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full border text-xs font-semibold ${tone}`} data-testid={`admin-status-${status || "unknown"}`}>{label}</span>;
}
